// @flow
import * as React from 'react'
import moment from 'moment'
import { FormattedMessage } from 'mz-intl'
import { Footer } from '../../../../core/LightboxBaseField'
import { Button, ButtonToolbar } from '../../../../core/Button'
import Selector from '../Selector'
import { SelectorsList, Container, ColonSeparator } from './elements'
import { is12hFormat } from '../../../../utils'
import commonMessages from '../../../../messages.intl'

// Types
import type { Props } from '..'

type TimePeriod = 'hour' | 'minute' | 'dayPeriod'

type State = {
  dateTime: moment,
}

/**
 * Separate lightbox component to keep intermediate selected time in
 * the component state. The intermediate value initiated only when the lightbox
 * opened.
 * @extends React
 */
class Lightbox extends React.Component<*, State> {
  state = {
    dateTime: moment().startOf('day'),
  }

  componentDidMount() {
    this.initValue(this.props)
  }

  initValue(props: Props) {
    const {
      input: { value },
    } = props

    const trimmedDateTime = value
      ? value.startOf('minute')
      : moment().startOf('day')

    this.setState({ dateTime: trimmedDateTime })
  }

  roundMinutes = (minutes: number, roundDirection: 'up' | 'down'): number => {
    const { roundMinutes, roundMinutesInterval } = this.props

    if (!roundMinutes) return minutes

    const roundFunc = roundDirection === 'up' ? Math.ceil : Math.floor

    const roundedMinutes =
      roundFunc(minutes / roundMinutesInterval) * roundMinutesInterval

    return roundedMinutes
  }

  handleChangeTime = (duration: number, timePeriod: TimePeriod) => () => {
    const { dateTime } = this.state

    const durationDevider = is12hFormat() ? 12 : 24
    const addedDuration =
      timePeriod === 'hour'
        ? Math.abs(duration) !== durationDevider
          ? duration % durationDevider
          : duration
        : duration

    const newTime = dateTime.clone().add(addedDuration, timePeriod)

    const roundType = duration > 0 ? 'up' : 'down'

    // skip changing AP/PM when  changing hours
    const newTimeHours =
      timePeriod === 'hour' ? newTime.hours() : dateTime.hours()

    // round only when minutes changed
    const newTimeMinutes =
      timePeriod === 'minute'
        ? this.roundMinutes(newTime.minutes(), roundType)
        : newTime.minutes()

    // skip changing hours when changing minutes
    const newDateTime = dateTime
      .clone()
      .hours(newTimeHours)
      .minutes(newTimeMinutes === 60 ? 0 : newTimeMinutes)

    this.setState({ dateTime: newDateTime })
  }

  handleChangeInputValue = ({ hours, minutes, dayPeriod }: Object) => {
    const { dateTime: currentTime } = this.state

    const dayPeriodFromHours = hours >= 12 ? 'PM' : 'AM'
    const timeHours = hours || currentTime.format('hh')
    const timeMinutes = minutes || currentTime.format('mm')
    const timeDayPeriod =
      is12hFormat() || !hours
        ? dayPeriod || currentTime.format('A')
        : dayPeriodFromHours

    const enteredTime = moment(
      `${timeHours}:${timeMinutes} ${timeDayPeriod}`,
      'hh:mm A'
    )

    const enteredTimeMinutes = minutes
      ? this.roundMinutes(enteredTime.minutes(), 'up')
      : enteredTime.minutes()

    const newDateTime = currentTime
      .clone()
      .hours(enteredTime.hours())
      .minutes(enteredTimeMinutes === 60 ? 0 : enteredTimeMinutes)

    this.setState({ dateTime: newDateTime })
  }

  handleClickSelect = () => {
    this.props.onSelect(this.state.dateTime)
  }

  render() {
    return (
      <Container>
        <SelectorsList>
          <Selector
            innerRef={this.props.setFocusableRef}
            pattern="^([0-1]{0,1}[0-9]{0,1}|[2]{0,1}[0-3]{0,1})$"
            valueProp="hours"
            value={this.state.dateTime.format(is12hFormat() ? 'hh' : 'HH')}
            onChange={this.handleChangeInputValue}
            onClickUp={this.handleChangeTime(1, 'hour')}
            onClickDown={this.handleChangeTime(-1, 'hour')}
          />
          <ColonSeparator />
          <Selector
            pattern="^[0-5]{0,1}[0-9]{0,1}$"
            valueProp="minutes"
            value={this.state.dateTime.format('mm')}
            onChange={this.handleChangeInputValue}
            onClickUp={this.handleChangeTime(1, 'minute')}
            onClickDown={this.handleChangeTime(-1, 'minute')}
          />
          {is12hFormat() && (
            <Selector
              pattern="^[APap]{0,1}[mM]{0,1}$"
              valueProp="dayPeriod"
              value={this.state.dateTime.format('A')}
              onChange={this.handleChangeInputValue}
              onClickUp={this.handleChangeTime(12, 'hour')}
              onClickDown={this.handleChangeTime(-12, 'hour')}
            />
          )}
        </SelectorsList>
        <Footer>
          <ButtonToolbar>
            <Button appearance="flat" onClick={this.props.onCancel} fullWidth>
              <FormattedMessage message={commonMessages.CANCEL} />
            </Button>
            <Button onClick={this.handleClickSelect} fullWidth>
              <FormattedMessage message={commonMessages.SET} />
            </Button>
          </ButtonToolbar>
        </Footer>
      </Container>
    )
  }
}

export default Lightbox
